import i18n from "../i18n";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Menu,
  Modal,
  NumberInput,
  Select,
  Stack,
  Paper,
  Table,
  Text,
  TextInput,
  ActionIcon,
  Loader,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconBook, IconCopy, IconDotsVertical, IconPlus, IconTrash } from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import {
  buildEmptyRateObject,
  computeScheduleRatePercents,
  decimalToPercent,
  getYearColumnDefs,
  getYearKeys,
  percentToDecimal,
  scheduleHasCoverRate,
} from "../helpers/commissionScheduleYears";
import { DATA_CURRENCY, DATA_PAY_MODES } from "../data/options";
import {
  blankCondition,
  conditionFieldOptions,
  getConditionField,
  operatorsForField,
  parseConditionList,
  serializeConditionList,
} from "../helpers/commissionConditionFields";
import { toCommissionDatePickerValue } from "../helpers/commissionCalendarDate";

const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const toDateValue = (value) => toCommissionDatePickerValue(value);

const asDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const toDateInput = (value) => {
  const date = asDate(value);
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const ratesFromDecimal = (rate) => {
  const result = buildEmptyRateObject(0);
  for (const key of getYearKeys()) {
    result[key] = decimalToPercent(rate?.[key]);
  }
  return result;
};

const ratesToDecimal = (rate) => {
  const result = {};
  for (const key of getYearKeys()) {
    result[key] = percentToDecimal(rate?.[key]);
  }
  return result;
};

const blankRow = () => ({
  conditions: [],
  basicRate: buildEmptyRateObject(0),
  overrideRate: buildEmptyRateObject(0),
  extraRate: buildEmptyRateObject(0),
  coverRate: buildEmptyRateObject(0),
});

const blankForm = () => ({
  effectiveDate: today(),
  endDate: null,
  professionalInvestor: "false",
  rows: [blankRow()],
});

const formFromScheduleProduct = (scheduleProduct, mode) => {
  const rows = (scheduleProduct?.commissionTable || []).map((row) => ({
    conditions: parseConditionList(row.conditions),
    basicRate: ratesFromDecimal(row.basicRate),
    overrideRate: ratesFromDecimal(row.overrideRate),
    extraRate: ratesFromDecimal(row.extraRate),
    coverRate: ratesFromDecimal(row.coverRate),
    hasDiscountRate: row.hasDiscountRate === true,
  }));

  return {
    effectiveDate: mode === "create" ? today() : toDateValue(scheduleProduct?.effectiveDate),
    endDate: mode === "create" ? null : toDateValue(scheduleProduct?.endDate),
    professionalInvestor: scheduleProduct?.professionalInvestor ? "true" : "false",
    rows: rows.length ? rows : [blankRow()],
  };
};

const formatPercent = (value) => `${Number(value || 0).toFixed(4)}%`;

const sectionDivider = {
  borderLeft: "3px solid #343a40",
};

const dropdownProps = {
  withinPortal: true,
  zIndex: 1000,
  dropdownPosition: "flip",
  maxDropdownHeight: 480,
};

const formatAmountConditionValue = (value) => {
  if (value == null || value === "") return "";
  const text = String(value);
  const negative = text.startsWith("-");
  const digits = text.replace(/[^\d]/g, "");
  if (!digits) return negative ? "-" : "";
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return negative ? `-${grouped}` : grouped;
};

const parseAmountConditionValue = (value) => String(value ?? "").replace(/[^\d]/g, "");

const FORM_GUIDE = [
  {
    term: "Start date",
    text: "The day this commission version starts. If the previous version for the same PI type has no end date, it ends the day before this start date.",
  },
  {
    term: "End date",
    text: "Optional. The last day this version applies. Leave it empty to keep the version open.",
  },
  {
    term: "PI",
    text: "Professional investor. PI and Non-PI are separate versions. An order uses the version that matches the signed date and whether the client is a professional investor.",
  },
  {
    term: "Conditions",
    text: "Rules that choose this rate row, such as insured age, payment mode, or premium. A row can have more than one condition.",
  },
  {
    term: "Basic Rate",
    text: "The base commission percentage you enter for each policy year.",
  },
  {
    term: "Override Rate",
    text: "A multiple applied to the basic rate. Total uses (Override + 1) × Basic.",
  },
  {
    term: "Extra Rate",
    text: "An extra percentage added after the override calculation.",
  },
  {
    term: "Total Rate",
    text: "Calculated, not typed. (Override + 1) × Basic + Extra.",
  },
  {
    term: "Gross Rate",
    text: "Calculated from Total. Each year is capped at 100%, and anything over 100% is added to the next year. Insurer commission statement matching uses this rate.",
  },
  {
    term: "Discount Rate",
    text: "Optional row under the gross rate. Once it is saved, every year uses the discount rate, including 0. A year set to 0 dispatches no consultant commission for that year. Insurer statement matching still uses the original gross rate, and the difference is added to the remain layer.",
  },
  {
    term: "Y1–Y11+",
    text: "Policy year. Y1 is the first year. Y11+ is year 11 and every year after it.",
  },
];

const RATE_SECTIONS = [
  { key: "grossRate", label: "Gross Rate(%)", editable: false, color: "#FFC000" },
  { key: "totalRate", label: "Total Rate(%)", editable: false, color: "#FFC000" },
  { key: "basicRate", label: "Basic Rate(%)", editable: true, color: "#92D050" },
  { key: "overrideRate", label: "Override Rate(%)", editable: true, color: "#92D050" },
  { key: "extraRate", label: "Extra Rate(%)", editable: true, color: "#92D050" },
];

const ProductCommissionScheduleEditor = ({
  mode = "create",
  productId,
  scheduleProductId = null,
  versionName = "",
  onClose,
  onSaved,
}) => {
  const [api] = useServerApi();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearRatesOpen, setClearRatesOpen] = useState(false);
  const [clearRatesChecked, setClearRatesChecked] = useState(false);
  const [showDiscountRate, setShowDiscountRate] = useState(false);
  const [removeDiscountOpen, setRemoveDiscountOpen] = useState(false);
  const [unsavedCloseOpen, setUnsavedCloseOpen] = useState(false);
  const [guidelineOpen, setGuidelineOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const savedFormRef = useRef(null);
  if (savedFormRef.current == null) savedFormRef.current = JSON.stringify(form);
  const yearColumns = getYearColumnDefs();

  useEffect(() => {
    let cancelled = false;

    const rememberSavedForm = (nextForm) => {
      savedFormRef.current = JSON.stringify(nextForm);
    };

    const load = async () => {
      if (!scheduleProductId) {
        const nextForm = blankForm();
        setForm(nextForm);
        rememberSavedForm(nextForm);
        setShowDiscountRate(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const scheduleProduct = await api.getById({
          apiEntity: "commissionScheduleProduct",
          id: scheduleProductId,
        });
        if (cancelled) return;
        if (!scheduleProduct) throw new Error("Commission version not found");
        const nextForm = formFromScheduleProduct(scheduleProduct, mode);
        setForm(nextForm);
        rememberSavedForm(nextForm);
        setShowDiscountRate(
          scheduleHasCoverRate(scheduleProduct.commissionTable) ||
            nextForm.rows.some((row) =>
              getYearKeys().some((key) => Number(row.coverRate?.[key]) > 0)
            )
        );
      } catch (error) {
        if (!cancelled) {
          showNotification({
            color: "red",
            title: "Unable to load commission",
            message: error?.message || "Please try again",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [scheduleProductId, mode]);

  const updateRow = (rowIndex, patch) => {
    setForm((current) => ({
      ...current,
      rows: current.rows.map((row, index) =>
        index === rowIndex ? { ...row, ...patch } : row
      ),
    }));
  };

  const updateConditions = (rowIndex, conditions) => {
    updateRow(rowIndex, { conditions });
  };

  const updateCondition = (rowIndex, conditionIndex, patch) => {
    const conditions = form.rows[rowIndex].conditions.map((condition, index) => {
      if (index !== conditionIndex) return condition;
      const next = { ...condition, ...patch };
      if (patch.field && patch.field !== condition.field) {
        const field = getConditionField(patch.field);
        const operators = operatorsForField(patch.field);
        next.operator = operators[0]?.value || "";
        next.value = "";
        next.customField = "";
        next.unrecognized = false;
        next.raw = "";
        if (field?.valueType === "number" && !operators.some((item) => item.value === "≥")) {
          next.operator = operators[0]?.value || "=";
        }
      }
      return next;
    });
    updateConditions(rowIndex, conditions);
  };

  const updateRate = (rowIndex, rateKey, yearKey, value) => {
    const number = Number(value);
    setForm((current) => ({
      ...current,
      rows: current.rows.map((row, index) => {
        if (index !== rowIndex) return row;
        return {
          ...row,
          [rateKey]: {
            ...row[rateKey],
            [yearKey]: Number.isFinite(number) ? number : 0,
          },
        };
      }),
    }));
  };

  const addRow = () => {
    setForm((current) => ({ ...current, rows: [...current.rows, blankRow()] }));
  };

  const addDiscountRate = () => {
    setForm((current) => ({
      ...current,
      rows: current.rows.map((row) => {
        const hasDiscount = getYearKeys().some((key) => Number(row.coverRate?.[key]) > 0);
        if (hasDiscount) return row;
        const preview = computeScheduleRatePercents(
          row.basicRate,
          row.overrideRate,
          row.extraRate
        );
        return { ...row, coverRate: { ...preview.gross } };
      }),
    }));
    setShowDiscountRate(true);
  };

  const removeDiscountRate = () => {
    setForm((current) => ({
      ...current,
      rows: current.rows.map((row) => ({
        ...row,
        coverRate: buildEmptyRateObject(0),
      })),
    }));
    setShowDiscountRate(false);
    setRemoveDiscountOpen(false);
  };

  const openClearRates = () => {
    setClearRatesChecked(false);
    setClearRatesOpen(true);
  };

  const closeClearRates = () => {
    setClearRatesOpen(false);
    setClearRatesChecked(false);
  };

  const clearAllRates = () => {
    if (!clearRatesChecked) return;
    setForm((current) => ({
      ...current,
      rows: current.rows.map((row) => ({
        ...row,
        basicRate: buildEmptyRateObject(0),
        overrideRate: buildEmptyRateObject(0),
        extraRate: buildEmptyRateObject(0),
        coverRate: buildEmptyRateObject(0),
      })),
    }));
    closeClearRates();
  };

  const copyRow = (rowIndex) => {
    setForm((current) => {
      const source = current.rows[rowIndex];
      if (!source) return current;
      const copy = {
        conditions: source.conditions.map((condition) => ({ ...condition })),
        basicRate: { ...source.basicRate },
        overrideRate: { ...source.overrideRate },
        extraRate: { ...source.extraRate },
        coverRate: { ...source.coverRate },
      };
      const rows = [...current.rows];
      rows.splice(rowIndex + 1, 0, copy);
      return { ...current, rows };
    });
  };

  const removeRow = (rowIndex) => {
    setForm((current) => ({
      ...current,
      rows: current.rows.filter((_, index) => index !== rowIndex),
    }));
  };

  const save = async () => {
    if (!productId) return;
    const effectiveDate = asDate(form.effectiveDate);
    const endDate = asDate(form.endDate);
    if (!effectiveDate) {
      showNotification({
        color: "red",
        title: "Start date is required",
        message: "Choose the date this commission version starts.",
      });
      return;
    }
    if (form.endDate && !endDate) {
      showNotification({
        color: "red",
        title: "End date is invalid",
        message: "Use a date in YYYY-MM-DD format.",
      });
      return;
    }
    if (endDate && endDate < effectiveDate) {
      showNotification({
        color: "red",
        title: "End date is before the start date",
        message: "The end date must be on or after the start date.",
      });
      return;
    }
    if (showDiscountRate) {
      const coverTooHigh = form.rows.some((row) => {
        const preview = computeScheduleRatePercents(
          row.basicRate,
          row.overrideRate,
          row.extraRate
        );
        return getYearKeys().some((key) => {
          const cover = Number(row.coverRate?.[key]) || 0;
          return cover > Number(preview.gross[key]);
        });
      });
      if (coverTooHigh) {
        showNotification({
          color: "red",
          title: "Discount rate is too high",
          message: "A discount rate cannot be higher than that year's gross rate. A year set to 0 dispatches no commission for that year.",
        });
        return;
      }
    }

    const body = {
      effectiveDate: toDateInput(effectiveDate),
      endDate: toDateInput(endDate),
      professionalInvestor: form.professionalInvestor === "true",
      commissionTable: form.rows.map((row) => ({
        conditions: serializeConditionList(row.conditions),
        basicRate: ratesToDecimal(row.basicRate),
        overrideRate: ratesToDecimal(row.overrideRate),
        extraRate: ratesToDecimal(row.extraRate),
        coverRate: ratesToDecimal(row.coverRate),
        hasDiscountRate: showDiscountRate,
      })),
    };

    setSaving(true);
    try {
      if (mode === "edit") {
        await api.Product.updateCommissionScheduleProduct(productId, {
          ...body,
          scheduleProductId,
        });
      } else {
        await api.Product.createCommissionScheduleProduct(productId, {
          ...body,
          sourceScheduleProductId: scheduleProductId || undefined,
        });
      }
      showNotification({
        color: "teal",
        title: mode === "edit" ? "Commission updated" : "Commission version added",
        message:
          mode === "edit"
            ? "This product commission version has been saved."
            : "The new commission version is saved for this product.",
      });
      onSaved?.(mode);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Unable to save commission",
        message: error?.message || "Please try again",
      });
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (saving) return;
    if (JSON.stringify(form) !== savedFormRef.current) {
      setUnsavedCloseOpen(true);
      return;
    }
    onClose?.();
  };

  const displayRate = (row, tabValue, yearKey) => {
    if (tabValue === "totalRate" || tabValue === "grossRate") {
      const preview = computeScheduleRatePercents(
        row.basicRate,
        row.overrideRate,
        row.extraRate
      );
      return formatPercent(tabValue === "totalRate" ? preview.total[yearKey] : preview.gross[yearKey]);
    }
    return null;
  };

  return (
    <>
    <Modal
      opened
      onClose={requestClose}
      title={mode === "edit" ? "Edit product commission" : "Add product commission"}
      fullScreen
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
    <Stack spacing="md">
      <Group position="apart" align="flex-start">
        <div>
          <Text weight={600}>
            {mode === "edit" ? "Edit commission" : "Add commission"}
            {versionName ? ` ${versionName}` : ""}
          </Text>
          <Text size="xs" color="dimmed" mt={4}>
            Enter Basic, Override, and Extra as percentages. Gross and Total are calculated. Gross is capped at 100% each year, and anything over 100% is added to the next year.
          </Text>
        </div>
        <Group spacing="xs">
          <Button variant="default" onClick={requestClose} disabled={saving}>{i18n.t("Cancel")}</Button>
          <Button onClick={save} loading={saving}>{i18n.t("Save")}</Button>
        </Group>
      </Group>

      {loading ? (
        <Group position="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <>
          <Alert color={mode === "edit" ? "orange" : "blue"} variant="light">
            {mode === "edit"
              ? "Saving updates this commission version. Orders that already saved these rates keep them until commission is regenerated."
              : "This adds a commission version for this product only. The imported schedule file stays unchanged. If the previous version for the same PI type has no end date, it ends the day before this start date."}
          </Alert>

          <Group align="flex-end" spacing="md">
            <DatePicker
              label="Start"
              placeholder="Start date"
              value={form.effectiveDate}
              onChange={(value) =>
                setForm((current) => ({ ...current, effectiveDate: value }))
              }
              inputFormat="YYYY-MM-DD"
              allowFreeInput
              clearable={false}
              required
              style={{ width: 180 }}
            />
            <DatePicker
              label="End"
              placeholder="No end date"
              value={form.endDate}
              onChange={(value) =>
                setForm((current) => ({ ...current, endDate: value }))
              }
              inputFormat="YYYY-MM-DD"
              allowFreeInput
              clearable
              style={{ width: 180 }}
            />
            <Select
              label="Investor type"
              data={[
                { value: "false", label: "Non-PI" },
                { value: "true", label: "PI" },
              ]}
              value={form.professionalInvestor}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  professionalInvestor: value || "false",
                }))
              }
              style={{ width: 160 }}
            />
          </Group>

          <div style={{ overflowX: "auto", width: "100%" }}>
            <Table
              fontSize="xs"
              verticalSpacing="xs"
              sx={{
                width: "max-content",
                minWidth: "100%",
                "th, td": { whiteSpace: "nowrap", verticalAlign: "middle" },
              }}
            >
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    style={{
                      minWidth: 220,
                      background: "#C6EFCE",
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                    }}
                  >{i18n.t("Conditions")}</th>
                  {RATE_SECTIONS.map((section) => (
                    <th
                      key={section.key}
                      colSpan={yearColumns.length}
                      style={{
                        background: section.color,
                        textAlign: "center",
                        ...sectionDivider,
                      }}
                    >
                      {section.label}
                    </th>
                  ))}
                  <th rowSpan={2} style={{ background: "#C6EFCE" }} />
                </tr>
                <tr>
                  {RATE_SECTIONS.map((section) =>
                    yearColumns.map((column, columnIndex) => (
                      <th
                        key={`${section.key}-${column.key}`}
                        style={{
                          background: "#C6EFCE",
                          textAlign: "center",
                          ...(columnIndex === 0 ? sectionDivider : null),
                        }}
                      >
                        {column.label}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {form.rows.map((row, rowIndex) => (
                  <Fragment key={rowIndex}>
                  <tr>
                    <td
                      rowSpan={showDiscountRate ? 2 : 1}
                      style={{
                        minWidth: 460,
                        background: "white",
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                      }}
                    >
                      <Stack spacing={6}>
                        {row.conditions.map((condition, conditionIndex) => {
                          const field = getConditionField(condition.field);
                          const operators = operatorsForField(condition.field);
                          return (
                            <Group
                              key={conditionIndex}
                              spacing={4}
                              noWrap
                              align="flex-end"
                            >
                              {condition.unrecognized ? (
                                <TextInput
                                  aria-label={`Unrecognized condition ${rowIndex + 1} ${conditionIndex + 1}`}
                                  value={condition.raw}
                                  readOnly
                                  size="xs"
                                  styles={{ input: { width: 280 } }}
                                />
                              ) : (
                                <>
                                  <Select
                                    aria-label={`Condition field ${rowIndex + 1} ${conditionIndex + 1}`}
                                    data={conditionFieldOptions}
                                    value={condition.field}
                                    onChange={(value) =>
                                      updateCondition(rowIndex, conditionIndex, {
                                        field: value || "Insured Age",
                                      })
                                    }
                                    size="xs"
                                    searchable
                                    {...dropdownProps}
                                    styles={{ input: { width: 180 } }}
                                  />
                                  {operators.length > 0 && (
                                    <Select
                                      aria-label={`Condition operator ${rowIndex + 1} ${conditionIndex + 1}`}
                                      data={operators}
                                      value={condition.operator}
                                      onChange={(value) =>
                                        updateCondition(rowIndex, conditionIndex, {
                                          operator: value || "=",
                                        })
                                      }
                                      size="xs"
                                      {...dropdownProps}
                                      styles={{ input: { width: 70 } }}
                                    />
                                  )}
                                  {field?.valueType === "number" && (
                                    <NumberInput
                                      aria-label={`Condition value ${rowIndex + 1} ${conditionIndex + 1}`}
                                      value={condition.value === "" ? undefined : Number(condition.value)}
                                      onChange={(value) =>
                                        updateCondition(rowIndex, conditionIndex, {
                                          value: value == null || value === "" ? "" : String(value),
                                        })
                                      }
                                      min={0}
                                      hideControls
                                      size="xs"
                                      {...(field.format === "amount"
                                        ? {
                                            precision: 0,
                                            parser: parseAmountConditionValue,
                                            formatter: formatAmountConditionValue,
                                            styles: { input: { width: 140, textAlign: "right" } },
                                          }
                                        : { styles: { input: { width: 90 } } })}
                                    />
                                  )}
                                  {field?.valueType === "currency" && (
                                    <Select
                                      aria-label={`Condition value ${rowIndex + 1} ${conditionIndex + 1}`}
                                      data={DATA_CURRENCY}
                                      value={condition.value || null}
                                      onChange={(value) =>
                                        updateCondition(rowIndex, conditionIndex, {
                                          value: value || "",
                                        })
                                      }
                                      size="xs"
                                      searchable
                                      {...dropdownProps}
                                      styles={{ input: { width: 100 } }}
                                    />
                                  )}
                                  {field?.valueType === "payMode" && (
                                    <Select
                                      aria-label={`Condition value ${rowIndex + 1} ${conditionIndex + 1}`}
                                      data={DATA_PAY_MODES}
                                      value={condition.value || null}
                                      onChange={(value) =>
                                        updateCondition(rowIndex, conditionIndex, {
                                          value: value || "",
                                        })
                                      }
                                      size="xs"
                                      {...dropdownProps}
                                      styles={{ input: { width: 140 } }}
                                    />
                                  )}
                                  {field?.valueType === "custom" && (
                                    <>
                                      <TextInput
                                        aria-label={`Custom field ${rowIndex + 1} ${conditionIndex + 1}`}
                                        placeholder="Field"
                                        value={condition.customField}
                                        onChange={(event) =>
                                          updateCondition(rowIndex, conditionIndex, {
                                            customField: event.currentTarget.value,
                                          })
                                        }
                                        size="xs"
                                        styles={{ input: { width: 90 } }}
                                      />
                                      <TextInput
                                        aria-label={`Custom value ${rowIndex + 1} ${conditionIndex + 1}`}
                                        placeholder="Value"
                                        value={condition.value}
                                        onChange={(event) =>
                                          updateCondition(rowIndex, conditionIndex, {
                                            value: event.currentTarget.value,
                                          })
                                        }
                                        size="xs"
                                        styles={{ input: { width: 90 } }}
                                      />
                                    </>
                                  )}
                                </>
                              )}
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                mb={2}
                                onClick={() =>
                                  updateConditions(
                                    rowIndex,
                                    row.conditions.filter((_, index) => index !== conditionIndex)
                                  )
                                }
                                aria-label={`Remove condition ${rowIndex + 1} ${conditionIndex + 1}`}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Group>
                          );
                        })}
                        <Group spacing={4}>
                          <Button
                            variant="subtle"
                            size="xs"
                            compact
                            leftIcon={<IconPlus size={12} />}
                            onClick={() =>
                              updateConditions(rowIndex, [...row.conditions, blankCondition()])
                            }
                          >
                            Add condition
                          </Button>
                          <Button
                            variant="subtle"
                            size="xs"
                            compact
                            leftIcon={<IconCopy size={12} />}
                            onClick={() => copyRow(rowIndex)}
                          >
                            Copy condition row
                          </Button>
                        </Group>
                      </Stack>
                    </td>
                    {RATE_SECTIONS.map((section) =>
                      yearColumns.map((column, columnIndex) => (
                        <td
                          key={`${section.key}-${column.key}`}
                          style={columnIndex === 0 ? sectionDivider : undefined}
                        >
                          {section.editable ? (
                            <NumberInput
                              aria-label={`${section.label} ${column.label} row ${rowIndex + 1}`}
                              value={row[section.key][column.key]}
                              onChange={(value) =>
                                updateRate(rowIndex, section.key, column.key, value)
                              }
                              precision={4}
                              min={0}
                              max={500}
                              step={0.0001}
                              hideControls
                              size="xs"
                              styles={{ input: { width: 88, textAlign: "right" } }}
                            />
                          ) : (
                            <Text size="xs" align="right">
                              {displayRate(row, section.key, column.key)}
                            </Text>
                          )}
                        </td>
                      ))
                    )}
                    <td rowSpan={showDiscountRate ? 2 : 1}>
                      {form.rows.length > 1 && (
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => removeRow(rowIndex)}
                          aria-label={`Remove condition row ${rowIndex + 1}`}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </td>
                  </tr>
                  {showDiscountRate && (
                    <tr>
                      {yearColumns.map((column, columnIndex) => (
                        <td
                          key={`discount-${column.key}`}
                          style={{
                            background: "#DEEBF7",
                            textAlign: "right",
                            ...(columnIndex === 0 ? sectionDivider : null),
                          }}
                        >
                          <NumberInput
                            aria-label={`Discount Rate ${column.label} row ${rowIndex + 1}`}
                            value={row.coverRate?.[column.key]}
                            onChange={(value) =>
                              updateRate(rowIndex, "coverRate", column.key, value)
                            }
                            precision={4}
                            min={0}
                            max={500}
                            step={0.0001}
                            hideControls
                            size="xs"
                            styles={{
                              root: { display: "flex", justifyContent: "flex-end" },
                              input: { width: 88, textAlign: "right" },
                            }}
                          />
                        </td>
                      ))}
                      <td
                        colSpan={yearColumns.length * (RATE_SECTIONS.length - 1)}
                        style={{ background: "#DEEBF7", ...sectionDivider }}
                      />
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </div>

          <Group position="apart">
            <Group spacing="xs">
              <Button variant="default" leftIcon={<IconPlus size={14} />} onClick={addRow}>
                Add condition row
              </Button>
              <Button
                variant="default"
                leftIcon={<IconBook size={14} />}
                onClick={() => setGuidelineOpen(true)}
              >
                {i18n.t("Guideline")}
              </Button>
              <Menu shadow="md" width={280} position="top-start" withinPortal={false}>
                <Menu.Target>
                  <ActionIcon variant="default" aria-label="More commission actions">
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {showDiscountRate ? (
                    <Menu.Item color="red" onClick={() => setRemoveDiscountOpen(true)}>
                      {i18n.t("Remove Discount Rate")}
                    </Menu.Item>
                  ) : (
                    <Menu.Item onClick={addDiscountRate}>
                      {i18n.t("Add Discount Rate")}
                    </Menu.Item>
                  )}
                  <Menu.Item color="red" onClick={openClearRates}>
                    {i18n.t("Clear All Rate")}
                  </Menu.Item>
                  <Text size="xs" color="dimmed" px="sm" pb="xs">
                    {i18n.t(
                      "Conditions will be kept, but all rates in the table will be set to 0."
                    )}
                  </Text>
                </Menu.Dropdown>
              </Menu>
            </Group>
            <Group spacing="xs">
              <Button variant="default" onClick={requestClose} disabled={saving}>{i18n.t("Cancel")}</Button>
              <Button onClick={save} loading={saving}>{i18n.t("Save")}</Button>
            </Group>
          </Group>
        </>
      )}
    </Stack>
    {guidelineOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="commission-guideline-title"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 400,
          background: "rgba(0, 0, 0, 0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={() => setGuidelineOpen(false)}
      >
        <Paper
          p="lg"
          shadow="xl"
          radius="md"
          style={{ width: 560, maxWidth: "92vw", maxHeight: "80vh", overflow: "auto" }}
          onClick={(event) => event.stopPropagation()}
        >
          <Stack spacing="sm">
            <Text id="commission-guideline-title" weight={700}>
              {i18n.t("Guideline")}
            </Text>
            {FORM_GUIDE.map((item) => (
              <div key={item.term}>
                <Text size="sm" weight={600}>
                  {i18n.t(item.term)}
                </Text>
                <Text size="sm" color="dimmed">
                  {i18n.t(item.text)}
                </Text>
              </div>
            ))}
            <Group position="right">
              <Button onClick={() => setGuidelineOpen(false)}>{i18n.t("Close")}</Button>
            </Group>
          </Stack>
        </Paper>
      </div>
    )}
    {unsavedCloseOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-commission-title"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 400,
          background: "rgba(0, 0, 0, 0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper p="lg" shadow="xl" radius="md" style={{ width: 440, maxWidth: "92vw" }}>
          <Stack>
            <Text id="unsaved-commission-title" weight={700}>
              {i18n.t("Unsaved changes")}
            </Text>
            <Text size="sm">
              {i18n.t("You have unsaved changes. If you close now, those changes will be lost.")}
            </Text>
            <Group position="right">
              <Button variant="default" onClick={() => setUnsavedCloseOpen(false)}>
                {i18n.t("Keep editing")}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setUnsavedCloseOpen(false);
                  onClose?.();
                }}
              >
                {i18n.t("Close without saving")}
              </Button>
            </Group>
          </Stack>
        </Paper>
      </div>
    )}
    {removeDiscountOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-discount-rate-title"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 20,
          background: "rgba(0, 0, 0, 0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper p="lg" shadow="xl" radius="md" style={{ width: 440, maxWidth: "92vw" }}>
          <Stack>
            <Text id="remove-discount-rate-title" weight={700} color="red">
              {i18n.t("Remove Discount Rate")}
            </Text>
            <Text size="sm">
              {i18n.t("This removes the discount rate from every condition row.")}
            </Text>
            <Group position="right">
              <Button variant="default" onClick={() => setRemoveDiscountOpen(false)}>
                {i18n.t("Cancel")}
              </Button>
              <Button color="red" onClick={removeDiscountRate}>
                {i18n.t("Remove Discount Rate")}
              </Button>
            </Group>
          </Stack>
        </Paper>
      </div>
    )}
    {clearRatesOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="clear-all-rates-title"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 20,
          background: "rgba(0, 0, 0, 0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper p="lg" shadow="xl" radius="md" style={{ width: 440, maxWidth: "92vw" }}>
          <Stack>
            <Text id="clear-all-rates-title" weight={700} color="red">
              {i18n.t("Clear All Rate")}
            </Text>
            <Alert color="red">
              {i18n.t("Conditions will be kept, but all rates in the table will be set to 0.")}
            </Alert>
            <Checkbox
              color="red"
              checked={clearRatesChecked}
              onChange={(event) => setClearRatesChecked(event.currentTarget.checked)}
              label={i18n.t("Clear all rates")}
            />
            <Group position="right">
              <Button variant="default" onClick={closeClearRates}>
                {i18n.t("Cancel")}
              </Button>
              <Button color="red" disabled={!clearRatesChecked} onClick={clearAllRates}>
                {i18n.t("Clear all rates")}
              </Button>
            </Group>
          </Stack>
        </Paper>
      </div>
    )}
    </Modal>
    </>
  );
};

export default ProductCommissionScheduleEditor;
